import { openDB, type IDBPDatabase } from "idb";

import type { Op, QueuedOp, SettledOp } from "./types";

// Pending changes live in IndexedDB so they survive reloads, closed tabs and restarts.
// If IndexedDB isn't available (some private modes), they're kept in memory only.

const DB_NAME = "ct-sync";
const PENDING = "pending";
const FAILED = "failed";
// How long a sent change keeps being shown on top of possibly older server data.
const SETTLE_MS = 2 * 60_000;

type Snapshot = { pending: QueuedOp[]; failed: QueuedOp[]; settled: SettledOp[]; persistent: boolean };

let dbPromise: Promise<IDBPDatabase | null> | null = null;
const memory: { pending: QueuedOp[]; failed: QueuedOp[]; seq: number } = { pending: [], failed: [], seq: 0 };
let settled: SettledOp[] = [];
let snapshot: Snapshot = { pending: [], failed: [], settled: [], persistent: true };
const listeners = new Set<() => void>();
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("ct-sync") : null;
channel?.addEventListener("message", () => void refresh());

function db(): Promise<IDBPDatabase | null> {
  dbPromise ??= openDB(DB_NAME, 1, {
    upgrade(d) {
      d.createObjectStore(PENDING, { keyPath: "seq", autoIncrement: true });
      d.createObjectStore(FAILED, { keyPath: "seq" });
    },
  }).catch(() => null);
  return dbPromise;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): Snapshot {
  return snapshot;
}

/** Reload from storage and notify the UI. */
export async function refresh(): Promise<void> {
  const d = await db();
  const now = Date.now();
  settled = settled.filter((s) => now - s.syncedAt < SETTLE_MS);
  snapshot = d
    ? {
        pending: (await d.getAll(PENDING)) as QueuedOp[],
        failed: (await d.getAll(FAILED)) as QueuedOp[],
        settled,
        persistent: true,
      }
    : { pending: [...memory.pending], failed: [...memory.failed], settled, persistent: false };
  listeners.forEach((l) => l());
}

function changed(): Promise<void> {
  channel?.postMessage("changed");
  return refresh();
}

export async function enqueue(userId: number, op: Op): Promise<void> {
  const item = { ...op, userId, createdAt: Date.now(), attempts: 0 };
  const d = await db();
  if (d) await d.add(PENDING, item);
  else memory.pending.push({ ...item, seq: ++memory.seq });
  await changed();
}

/** The oldest pending change for this user. Changes are always sent in order. */
export async function nextPending(userId: number): Promise<QueuedOp | undefined> {
  const d = await db();
  const all = d ? ((await d.getAll(PENDING)) as QueuedOp[]) : memory.pending;
  return all.filter((o) => o.userId === userId).sort((a, b) => a.seq - b.seq)[0];
}

export async function markSent(op: QueuedOp): Promise<void> {
  const d = await db();
  if (d) await d.delete(PENDING, op.seq);
  else memory.pending = memory.pending.filter((o) => o.seq !== op.seq);
  settled.push({ ...op, syncedAt: Date.now() });
  await changed();
}

export async function markAttempt(op: QueuedOp, error: string): Promise<void> {
  const updated = { ...op, attempts: op.attempts + 1, lastError: error };
  const d = await db();
  if (d) await d.put(PENDING, updated);
  else memory.pending = memory.pending.map((o) => (o.seq === op.seq ? updated : o));
  await changed();
}

/** The server rejected it; keep it aside so the user can see why, and move on. */
export async function markFailed(op: QueuedOp, error: string): Promise<void> {
  const failed = { ...op, attempts: op.attempts + 1, lastError: error };
  const d = await db();
  if (d) {
    const tx = d.transaction([PENDING, FAILED], "readwrite");
    await Promise.all([tx.objectStore(PENDING).delete(op.seq), tx.objectStore(FAILED).put(failed), tx.done]);
  } else {
    memory.pending = memory.pending.filter((o) => o.seq !== op.seq);
    memory.failed.push(failed);
  }
  await changed();
}

/** Put a failed change back in the queue (at the end). */
export async function retryFailed(op: QueuedOp): Promise<void> {
  await discardFailed(op);
  await enqueue(op.userId, toOp(op));
}

/** The change itself, without the queue bookkeeping. */
export function toOp(q: QueuedOp): Op {
  const op: Record<string, unknown> = { ...q };
  for (const key of ["seq", "userId", "createdAt", "attempts", "lastError", "syncedAt"]) delete op[key];
  return op as Op;
}

export async function discardFailed(op: QueuedOp): Promise<void> {
  const d = await db();
  if (d) await d.delete(FAILED, op.seq);
  else memory.failed = memory.failed.filter((o) => o.seq !== op.seq);
  await changed();
}

/** For tests: forget everything. */
export async function resetForTests(): Promise<void> {
  const d = await db();
  if (d) await Promise.all([d.clear(PENDING), d.clear(FAILED)]);
  memory.pending = [];
  memory.failed = [];
  settled = [];
  await refresh();
}

void refresh();
